/**
 * Listado de actas de recepción.
 *
 * Es la puerta de entrada cuando aparece un reclamo: se busca por patente y se
 * abre el acta de ese ingreso para ver cómo estaba el auto.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlassIcon, CameraIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tip } from '../components/ui/tip';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';

const VehicleReceptions: React.FC = () => {
  const navigate = useNavigate();
  const [actas, setActas] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async (q: string) => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/receptions', { params: q ? { search: q } : {} });
      setActas(data.actas || []);
    } catch {
      toast.error('No se pudieron cargar las actas');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => cargar(search), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search, cargar]);

  const fecha = (v: string) => {
    try {
      return new Date(v).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return v; }
  };

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Actas de recepción</h1>
        <p className="text-sm text-muted-foreground">
          Cómo entró cada vehículo al taller: fotos del estado, kilometraje y daños previos.
        </p>
      </div>

      <div className="relative max-w-xl">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por patente, cliente, N° de acta u OT..."
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="p-8 text-center text-muted-foreground">Cargando...</p>
          ) : actas.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">
              {search ? 'No hay actas que coincidan con la búsqueda.' : 'Todavía no hay actas. Se crean desde Taller, al recibir el vehículo.'}
            </p>
          ) : (
            <>
              <div className="hidden md:block cq-table">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-muted/50 border-b">
                      <TableHead className="h-10 px-2 text-left font-medium text-foreground whitespace-nowrap">Acta</TableHead>
                      <TableHead className="h-10 px-2 text-left font-medium text-foreground whitespace-nowrap">Fecha</TableHead>
                      <TableHead className="h-10 px-2 text-left font-medium text-foreground whitespace-nowrap">Vehículo</TableHead>
                      <TableHead className="h-10 px-2 text-left font-medium text-foreground whitespace-nowrap cq-hide-narrow">Cliente</TableHead>
                      <TableHead className="h-10 px-2 text-right font-medium text-foreground whitespace-nowrap cq-hide-narrow">Km</TableHead>
                      <TableHead className="h-10 px-2 text-left font-medium text-foreground whitespace-nowrap">Fotos</TableHead>
                      <TableHead className="h-10 px-2 text-right font-medium text-foreground whitespace-nowrap"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {actas.map((a) => (
                      <TableRow key={a.id} className="cursor-pointer" onClick={() => navigate(`/actas/${a.id}`)}>
                        <TableCell className="p-2 align-middle whitespace-nowrap font-mono text-sm">{a.receptionNumber}</TableCell>
                        <TableCell className="p-2 align-middle whitespace-nowrap text-sm">{fecha(a.createdAt)}</TableCell>
                        <TableCell className="p-2 align-middle">
                          <div className="font-medium">{a.licensePlate || 'Sin patente'}</div>
                          <div className="truncate text-xs text-muted-foreground">
                            {[a.make, a.model].filter(Boolean).join(' ')}{a.appointmentCode ? ` · ${a.appointmentCode}` : ''}
                          </div>
                        </TableCell>
                        <TableCell className="p-2 align-middle cq-hide-narrow"><span className="truncate">{a.clientName || '—'}</span></TableCell>
                        <TableCell className="p-2 align-middle text-right tabular-nums cq-hide-narrow">
                          {a.km != null ? Number(a.km).toLocaleString('es-AR') : '—'}
                        </TableCell>
                        <TableCell className="p-2 align-middle">
                          <Badge variant="secondary" className={(a.photos?.length || 0) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}>
                            <CameraIcon className="mr-1 h-3.5 w-3.5" />{a.photos?.length || 0}
                          </Badge>
                        </TableCell>
                        <TableCell className="p-2 align-middle text-right" onClick={(e) => e.stopPropagation()}>
                          <Tip label="Abrir acta">
                            <Button variant="outline" size="icon" className="size-8" onClick={() => navigate(`/actas/${a.id}`)}>
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-3 p-4 md:hidden">
                {actas.map((a) => (
                  <div key={a.id} onClick={() => navigate(`/actas/${a.id}`)} className="rounded-xl border bg-card/80 p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold">{a.licensePlate || 'Sin patente'}</p>
                        <p className="truncate text-sm text-muted-foreground">{a.clientName || '—'}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{a.receptionNumber} · {fecha(a.createdAt)}</p>
                      </div>
                      <Badge variant="secondary" className={(a.photos?.length || 0) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}>
                        <CameraIcon className="mr-1 h-3.5 w-3.5" />{a.photos?.length || 0}
                      </Badge>
                    </div>
                    {(a.photos?.length || 0) > 0 && (
                      <div className="mt-3 flex gap-1.5 overflow-x-auto">
                        {a.photos.slice(0, 5).map((p: any, i: number) => (
                          <img key={i} src={p.path} alt="" className="h-14 w-14 shrink-0 rounded border object-cover" />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleReceptions;
